import React from 'react';
import { useTranslation } from 'react-i18next';

/** Ratios returned by the forecast engine (results.ratios). Fractions: 0.25 = 25%. */
export interface Ratios {
    gross_margin: (number | null)[];
    ebitda_margin: (number | null)[];
    net_margin: (number | null)[];
    revenue_growth: (number | null)[];
    cash_runway_months: (number | null)[];
    breakeven_month: { index: number; label: string } | null;          // first month with a profit
    breakeven_dips_month?: { index: number; label: string } | null;    // first later loss month, if any
    cumulative_breakeven_month: { index: number; label: string } | null;
    no_startup_losses?: boolean;                                      // profitable from the start
}

export const pctText = (v: number | null | undefined): string =>
    (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);

/** Hook returning the labels / formatters shared by the table and the dashboard cards. */
export const useRatioText = () => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const tr = (key: string, fallback: string, opts?: any) => String(t(key, fallback, opts));
    const runway = (v: number | null | undefined) =>
        (v === null || v === undefined ? tr('ratios.cashPositive', 'Cash positive') : tr('ratios.months', '{{n}} months', { n: v }));
    const breakeven = (r: Ratios) => {
        if (!r.breakeven_month) return tr('ratios.notReached', 'Not reached in 5 years');
        return r.breakeven_dips_month
            ? `${r.breakeven_month.label} ${tr('ratios.dipsAgain', '(dips again in {{month}})', { month: r.breakeven_dips_month.label })}`
            : r.breakeven_month.label;
    };
    const recovered = (r: Ratios) => {
        if (r.no_startup_losses) return tr('ratios.noLosses', 'No start-up losses');
        return r.cumulative_breakeven_month ? r.cumulative_breakeven_month.label : tr('ratios.notReached', 'Not reached in 5 years');
    };
    return { tr, runway, breakeven, recovered };
};

/** Year 1-5 table of margins, growth and cash runway, plus the break-even months. */
const KeyRatios: React.FC<{ ratios?: Ratios }> = ({ ratios }) => {
    const { tr, runway, breakeven, recovered } = useRatioText();
    if (!ratios) return null;
    const rows: [string, string, (y: number) => string][] = [
        ['gm', tr('ratios.grossMargin', 'Gross margin %'), (y) => pctText(ratios.gross_margin[y])],
        ['em', tr('ratios.ebitdaMargin', 'EBITDA margin %'), (y) => pctText(ratios.ebitda_margin[y])],
        ['nm', tr('ratios.netMargin', 'Net margin %'), (y) => pctText(ratios.net_margin[y])],
        ['rg', tr('ratios.revenueGrowth', 'Revenue growth % (vs prior year)'), (y) => pctText(ratios.revenue_growth[y])],
        ['rw', tr('ratios.runway', 'Cash runway at year end'), (y) => runway(ratios.cash_runway_months[y])],
    ];
    return (
        <div className="fyf-panel">
            <h3>{tr('ratios.title', 'Key Ratios')}</h3>
            <div className="fyf-table-wrap fyf-results">
                <table>
                    <thead>
                        <tr>
                            <th>{tr('ratios.measure', 'Measure')}</th>
                            {[1, 2, 3, 4, 5].map((y) => <th key={y} className="num">{tr('common.year', 'Year {{n}}', { n: y })}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(([key, name, fn]) => (
                            <tr key={key}>
                                <td>{name}</td>
                                {[0, 1, 2, 3, 4].map((y) => <td key={y} className="num">{fn(y)}</td>)}
                            </tr>
                        ))}
                        <tr className="fyf-total">
                            <td>{tr('ratios.breakeven', 'Break-even month (first month with a profit)')}</td>
                            <td colSpan={5} className="num">{breakeven(ratios)}</td>
                        </tr>
                        <tr className="fyf-total">
                            <td>{tr('ratios.recovered', 'Start-up losses recovered (cumulative profit turns positive)')}</td>
                            <td colSpan={5} className="num">{recovered(ratios)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="fyf-note">
                {tr('ratios.rule', 'Margins are a % of revenue after adjustments. Cash runway = cash at year end ÷ average monthly cash used by operations and investing that year; "Cash positive" means the business is not using cash. Break-even is the first month with a profit (net income above zero); if a later month is a loss again, that month is shown too. Losses recovered is the month total profit since the start turns positive.')}
            </div>
        </div>
    );
};

export default KeyRatios;
